import React, {
  Fragment,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";

import FeedNewCard from "./FeedNewCard";
import FeedInterestCard from "./FeedInterestCard";
import ServiceRecommendationBanner from "./ServiceRecommendationBanner";
import TrendingNewsCarousel from "./TrendingNewsCarousel";
import TrendingNewsList from "./TrendingNewsList";
import FinancialNewsEvents from "./FinancialNewsEvents";
import FinancialEventsList from "./FinancialEventsList";
import EventNewsDetailsView from "./EventNewsDetailsView";

import {
  fetchUserFeed,
  fetchFinancialNews,
  fetchTrendingNews,
  fetchFinancialNewsEvents,
  recordInterestResponse,
} from "../../../api/feed/feedAPI";

import { getServiceRecommendations } from "../../../api/recommendations/recommendationAPI";

import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import LoadingToRealTransitionWrapper from "../../../components/ui/transitions/LoadingToRealTransitionWraper";
import GhostFeedNewCard from "../../../components/ui/loading/skeletons/GhostFeedNewCard";
import PillFilter from "../../../components/ui/filters/PillFilter";

const getRandomInterval = (min = 5, max = 9) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function Feed() {
  const FEED_OPTIONS = [
    { label: "Feed", value: "feed" },
    { label: "Stocks", value: "stocks" },
    { label: "Crypto", value: "crypto" },
  ];

  const [activeTab, setActiveTab] = useState("feed");

  // Controls views: null | 'trending' | 'events' | { type: 'single_event', event: item }
  const [activeFullView, setActiveFullView] = useState(null);

  // Scroll Restoration State
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);

  const [feedItems, setFeedItems] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [trendingNews, setTrendingNews] = useState([]);
  const [financialEvents, setFinancialEvents] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(true);

  const [loading, setLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [dismissedBanners, setDismissedBanners] = useState([]);

  const observer = useRef();

  /*
   * Helper to save current scroll position before pushing a subview
   */
  const captureScrollAndSetView = (viewTarget) => {
    const currentScroll =
      window.scrollY || document.documentElement.scrollTop || 0;
    setSavedScrollPosition(currentScroll);
    setActiveFullView(viewTarget);

    // Jump subview to top
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  /*
   * Restore scroll position when returning back to main feed view
   */
  useLayoutEffect(() => {
    if (activeFullView === null && savedScrollPosition > 0) {
      window.scrollTo({
        top: savedScrollPosition,
        behavior: "instant",
      });
    }
  }, [activeFullView, savedScrollPosition]);

  /*
   * Handle navigation clicks
   */
  const handleSeeAllTrending = () => {
    captureScrollAndSetView("trending");
  };

  const handleSeeAllEvents = () => {
    captureScrollAndSetView("events");
  };

  const handleEventClick = (eventItem) => {
    captureScrollAndSetView({ type: "single_event", event: eventItem });
  };

  const handleGoBack = () => {
    setActiveFullView(null);
  };

  /*
   * Infinite scroll
   */
  const lastElementRef = useCallback(
    (node) => {
      if (loading || isFetchingMore || activeFullView) return;

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          setPage((prevPage) => prevPage + 1);
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, isFetchingMore, hasNextPage, activeFullView],
  );

  /*
   * Change feed tab
   */
  const handleTabChange = (newTab) => {
    if (newTab === activeTab) return;

    setActiveTab(newTab);
    setError(null);
    setPage(1);
    setFeedItems([]);
    setSavedScrollPosition(0);
    setLoading(true);

    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  };

  /*
   * Load auxiliary data on mount (recommendations, trending news, & top 3 events)
   */
  useEffect(() => {
    const fetchInitialAuxiliaryData = async () => {
      try {
        const recRes = await getServiceRecommendations();
        if (recRes.success) {
          setRecommendations(recRes.data?.recommendations || []);
        }
      } catch (err) {
        console.error("Failed to load service recommendations:", err);
      }

      try {
        setTrendingLoading(true);
        const trendingRes = await fetchTrendingNews(10);
        if (trendingRes.success) {
          setTrendingNews(trendingRes.data || []);
        }
      } catch (err) {
        console.error("Failed to load trending news:", err);
      } finally {
        setTrendingLoading(false);
      }

      try {
        const eventsRes = await fetchFinancialNewsEvents(1, 3);
        if (eventsRes.success) {
          setFinancialEvents(eventsRes.data || []);
        }
      } catch (err) {
        console.error("Failed to load financial news events:", err);
      }
    };

    fetchInitialAuxiliaryData();
  }, []);

  /*
   * Load feed content
   */
  useEffect(() => {
    if (activeFullView) return;

    const loadContent = async () => {
      if (page === 1) {
        setLoading(true);
      } else {
        setIsFetchingMore(true);
      }

      try {
        let response;

        if (activeTab === "feed") {
          response = await fetchUserFeed(page);
        } else {
          response = await fetchFinancialNews({
            news_type: activeTab,
            page,
            limit: 10,
          });
        }

        if (response.success) {
          setFeedItems((prevItems) =>
            page === 1 ? response.data : [...prevItems, ...response.data],
          );

          setHasNextPage(response.pagination?.has_next || false);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError("An unexpected error occurred while loading content.");
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
      }
    };

    loadContent();
  }, [page, activeTab, activeFullView]);

  /*
   * Remove financial interest from feed
   */
  const removeItemFromFeed = (uuid) => {
    setFeedItems((prevItems) =>
      prevItems.filter((item) => item.content?.uuid !== uuid),
    );
  };

  /*
   * Dismiss service recommendation
   */
  const handleDismissBanner = (recommendationId) => {
    setDismissedBanners((prev) => {
      if (prev.includes(recommendationId)) {
        return prev;
      }

      return [...prev, recommendationId];
    });
  };

  /*
   * Financial interest submit
   */
  const handleInterestSubmit = async ({ uuid, selectedAnswer }) => {
    removeItemFromFeed(uuid);

    const response = await recordInterestResponse({
      interest_uuid: uuid,
      selected_answer: selectedAnswer,
      is_skipped: false,
    });

    if (!response.success) {
      console.error("Failed to sync interest response:", response.message);
    }
  };

  /*
   * Financial interest skip
   */
  const handleInterestSkip = async (uuid) => {
    removeItemFromFeed(uuid);

    const response = await recordInterestResponse({
      interest_uuid: uuid,
      selected_answer: null,
      is_skipped: true,
    });

    if (!response.success) {
      console.error("Failed to sync interest skip:", response.message);
    }
  };

  /*
   * Merge feed content with service recommendations, trending carousel, and events card.
   */
  const getRenderableItems = () => {
    if (activeTab !== "feed") {
      return feedItems;
    }

    const activeRecommendations = recommendations.filter(
      (recommendation) => !dismissedBanners.includes(recommendation.id),
    );

    const itemsWithInjections = [];
    let recommendationCounter = 0;
    let carouselCounter = 0;
    let eventsCounter = 0;

    const getNextRecommendationItem = () => {
      if (activeRecommendations.length === 0) return null;

      const rec =
        activeRecommendations[
          recommendationCounter % activeRecommendations.length
        ];

      const item = {
        item_type: "service_recommendation",
        id: `recommendation-${rec.id}-${recommendationCounter}`,
        recommendation_data: rec,
      };

      recommendationCounter++;
      return item;
    };

    const getTrendingCarouselItem = () => {
      const item = {
        item_type: "trending_news_carousel",
        id: `trending-news-carousel-${carouselCounter}`,
      };
      carouselCounter++;
      return item;
    };

    const getTrendingEventsItem = () => {
      if (financialEvents.length === 0) return null;

      const item = {
        item_type: "financial_news_events",
        id: `financial-news-events-card-${eventsCounter}`,
      };
      eventsCounter++;
      return item;
    };

    // 1. Fixed Top Element: Trending Carousel at Index 0
    itemsWithInjections.push(getTrendingCarouselItem());

    // 2. Fixed Top Element: First Service Recommendation at Index 1
    const initialRec = getNextRecommendationItem();
    if (initialRec) {
      itemsWithInjections.push(initialRec);
    }

    // 3. Periodic Counters & Intervals
    let itemsSinceLastRecommendation = 0;
    let itemsSinceLastCarousel = 0;
    let itemsSinceLastEvents = 0;

    let targetRecommendationInterval = getRandomInterval(5, 9);
    let targetCarouselInterval = getRandomInterval(6, 10);

    // Capped target on initial pass (index <= 4)
    let targetEventsInterval = getRandomInterval(1, 2);

    feedItems.forEach((item, index) => {
      itemsWithInjections.push(item);
      itemsSinceLastRecommendation++;
      itemsSinceLastCarousel++;
      itemsSinceLastEvents++;

      if (
        itemsSinceLastEvents >= targetEventsInterval &&
        index + 1 < feedItems.length
      ) {
        const eventsCard = getTrendingEventsItem();
        if (eventsCard) {
          itemsWithInjections.push(eventsCard);
        }
        itemsSinceLastEvents = 0;
        targetEventsInterval = getRandomInterval(3, 5);
      }

      if (
        itemsSinceLastRecommendation >= targetRecommendationInterval &&
        index + 1 < feedItems.length
      ) {
        const nextRec = getNextRecommendationItem();
        if (nextRec) {
          itemsWithInjections.push(nextRec);
        }
        itemsSinceLastRecommendation = 0;
        targetRecommendationInterval = getRandomInterval(5, 9);
      }

      if (
        itemsSinceLastCarousel >= targetCarouselInterval &&
        index + 1 < feedItems.length
      ) {
        itemsWithInjections.push(getTrendingCarouselItem());
        itemsSinceLastCarousel = 0;
        targetCarouselInterval = getRandomInterval(6, 10);
      }
    });

    return itemsWithInjections;
  };

  /*
   * Render feed
   */
  const renderContent = () => {
    if (activeFullView === "trending") {
      return <TrendingNewsList />;
    }

    if (activeFullView === "events") {
      return <FinancialEventsList onEventClick={handleEventClick} />;
    }

    if (activeFullView?.type === "single_event") {
      return (
        <EventNewsDetailsView
          eventUuid={activeFullView.event?.uuid}
          initialEventData={activeFullView.event}
        />
      );
    }

    if (error && feedItems.length === 0) {
      return <div className="feed-error-message">{error}</div>;
    }

    if (!loading && feedItems.length === 0) {
      return (
        <div className="feed-empty-message">
          No updates available at this time.
        </div>
      );
    }

    const listToRender = getRenderableItems();

    return (
      <LoadingToRealTransitionWrapper
        isLoading={loading || (page === 1 && feedItems.length === 0)}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "3px !important",
        }}
        ghost={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "3px", // Updated to match loaded items spacing
            }}
          >
            <GhostFeedNewCard />
            <GhostFeedNewCard />
          </div>
        }
      >
        {listToRender.map((item, index) => {
          const isLastItem = listToRender.length === index + 1;
          const isCarousel = item.item_type === "trending_news_carousel";

          let cardElement = null;

          if (item.item_type === "trending_news_carousel") {
            cardElement = (
              <TrendingNewsCarousel
                items={trendingNews}
                isLoading={trendingLoading}
                onSeeAllClick={handleSeeAllTrending}
              />
            );
          } else if (item.item_type === "financial_news_events") {
            cardElement = (
              <FinancialNewsEvents
                title="Trending Events"
                events={financialEvents}
                onEventClick={handleEventClick}
                onSeeAllClick={handleSeeAllEvents}
              />
            );
          } else if (item.item_type === "service_recommendation") {
            cardElement = (
              <ServiceRecommendationBanner
                recommendation={item.recommendation_data}
                onDismiss={() =>
                  handleDismissBanner(item.recommendation_data.id)
                }
              />
            );
          } else if (item.item_type === "financial_news") {
            cardElement = (
              <FeedNewCard
                id={item.content.uuid}
                title={item.content.title}
                description={item.content.description}
                date={item.content.new_date}
                image_url={item.content.image}
                source_link={item.content.source_link}
                source_name={item.content.source_name}
                likes={item.content.likes}
                dislikes={item.content.dislikes}
                loves={item.content.love}
                userReaction={item.content.user_reaction}
                comments={item.content.comments}
              />
            );
          } else if (item.item_type === "financial_interest") {
            cardElement = (
              <FeedInterestCard
                uuid={item.content.uuid}
                category={item.content.category}
                heading={item.content.heading}
                description={item.content.description}
                type={item.content.type}
                possibleAnswers={item.content.possible_answers}
                onSubmit={handleInterestSubmit}
                onSkip={handleInterestSkip}
              />
            );
          }

          if (cardElement) {
            return (
              <div
                key={item.id || item.content?.uuid || index}
                ref={isLastItem ? lastElementRef : null}
                className={`feed-item-wrapper ${
                  isCarousel ? "is-carousel-wrapper" : ""
                }`}
              >
                {cardElement}
              </div>
            );
          }

          return null;
        })}
      </LoadingToRealTransitionWrapper>
    );
  };

  const getHeaderTitle = () => {
    if (activeFullView === "events") return "Trending Events";
    if (activeFullView === "trending") return "Trending News";
    if (activeFullView?.type === "single_event") return "Event Stories";
    return "";
  };

  return (
    <Fragment>
      {/* Reusable Sticky Header Container */}
      <div className="feed-header-filter-container">
        {!activeFullView ? (
          <PillFilter
            className="feed-type-filter"
            options={FEED_OPTIONS}
            activeValue={activeTab}
            onChange={handleTabChange}
          />
        ) : (
          <div className="trending-news-header">
            <button
              type="button"
              className="cta-button secondary rounded small btn-fit-content"
              onClick={handleGoBack}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/arrow_right_alt.svg`}
                alt="Go Back"
                style={{
                  transform: "rotate(180deg)",
                  width: "18px",
                  height: "18px",
                }}
              />
            </button>

            <h2 className="trending-news-title">{getHeaderTitle()}</h2>
          </div>
        )}
      </div>

      {renderContent()}

      {!activeFullView && isFetchingMore && (
        <div style={{ padding: "20px 0" }}>
          <LoadingPlaceholder rows={1} type="card" />
        </div>
      )}
    </Fragment>
  );
}

export default Feed;
